// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@ces/App/Store/Reducers";
import Tooltip, { TooltipPresentationProps } from "@ces/MobiusOverrides/Tooltip/Tooltip";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";

interface OwnProps {
    product: ProductModel;
    extendedStyles?: ProductQtyInfoStyles;
    showPlaceholder?: boolean;
    selectedUOMLineId?: string;
}

type Props = OwnProps;

export interface ProductQtyInfoStyles {
    wrapper?: InjectableCss;
    minOrderedQtyWrapper?: InjectableCss;
    multipleSaleQtyWrapper?: InjectableCss;
    labelValue?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

export const productQtyInfoStyles: ProductQtyInfoStyles = {};

const ProductQtyInfo = ({ product, extendedStyles, showPlaceholder = true, selectedUOMLineId }: Props) => {
    const [styles] = React.useState(() => mergeToNew(productQtyInfoStyles, extendedStyles));

    const qtyPerBaseUnitOfMeasure = useAppSelector(
        state => (state as CustomAppState).xSelectedUom.multiplesOfByProductId[selectedUOMLineId || product.id],
    );

    const minQty = product.minimumOrderQty;
    const uoms = product.unitOfMeasures ?? [];
    const hasMultipleUoms = uoms.filter(uom => uom.unitOfMeasure && !uom.isDefault).length > 1;

    // show minSelPack when hasMultipleUoms if not show if > 1
    const shouldShowMinSellPack = (hasMultipleUoms && minQty > 0) || (!hasMultipleUoms && minQty > 1);
    // hide multiples of if it has the same value as min sell pack
    const shouldShowMultiplesOf = qtyPerBaseUnitOfMeasure > 0 && qtyPerBaseUnitOfMeasure !== minQty;

    if (!showPlaceholder && !shouldShowMinSellPack && !shouldShowMultiplesOf) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {shouldShowMinSellPack && !shouldShowMultiplesOf && (
                <StyledWrapper {...styles.minOrderedQtyWrapper}>
                    <Typography {...styles.labelValue}>
                        <b>{translate("Min. Sell Pack")}:</b>
                        <Typography>{product.minimumOrderQty}</Typography>
                    </Typography>
                    <Tooltip
                        {...styles.tooltip}
                        appendTo={document.body}
                        text={siteMessage("Custom_Product_MinimumOrderedQty").toString()}
                    />
                </StyledWrapper>
            )}

            {shouldShowMultiplesOf && (
                <StyledWrapper {...styles.multipleSaleQtyWrapper}>
                    <Typography {...styles.labelValue}>
                        <b>{translate("Multiples of")}:</b>
                        <Typography>{qtyPerBaseUnitOfMeasure}</Typography>
                    </Typography>
                    <Tooltip
                        {...styles.tooltip}
                        appendTo={document.body}
                        text={siteMessage("Custom_Product_MultipleSalesQty").toString()}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default ProductQtyInfo;
